import type { ComponentPropsWithRef, ReactNode } from "react";
import { Fragment, forwardRef } from "react";
import { BlockSection } from "@/react/components/shared/BuilderUI/BlockSection";
import { useTopBlockStyles } from "../../../Hooks/useTopBlockStyles";
import type { StyleValue } from "../../../StyleValue.types";
import { salesSecondaryBlockDefaultStyles } from "../constants";

export interface SalesBlock12Props
  extends Omit<ComponentPropsWithRef<"div">, "content"> {
  button?: ReactNode;
  buttonDescription?: ReactNode;
  content?: ReactNode;
  footer?: ReactNode;
  header?: ReactNode;
  styles?: Record<string, StyleValue>;
}

export const SalesBlock12 = forwardRef<HTMLDivElement, SalesBlock12Props>(
  (
    {
      header,
      content,
      button,
      buttonDescription,
      footer,
      className,
      styles,
      ...rest
    },
    ref,
  ) => {
    const { backgroundStyles, paddingStyles, marginStyles } = useTopBlockStyles(
      {
        styles,
        defaultStyles: salesSecondaryBlockDefaultStyles,
      },
    );

    return (
      <BlockSection ref={ref} className={className} {...rest}>
        <BlockSection.Background
          className="wb-bg-secondary"
          style={backgroundStyles}
        >
          <BlockSection.Container>
            <BlockSection.Content
              style={{
                ...paddingStyles,
                ...marginStyles,
              }}
            >
              <BlockSection.NestedBlock className="wb-mx-auto wb-flex wb-flex-col wb-items-center wb-gap-4 wb-text-center md:wb-gap-6 wb-max-w-2xl">
                {header}
              </BlockSection.NestedBlock>
              <div className="wb-mx-auto wb-flex wb-flex-col wb-items-center wb-gap-4 md:wb-gap-6 wb-text-center">
                <div className="wb-bg-primary wb-flex wb-flex-col wb-gap-6 wb-rounded-3xl wb-px-6 wb-py-5 md:wb-gap-9 md:wb-px-9 md:wb-py-10">
                  <BlockSection.NestedBlock className="wb-flex wb-flex-col wb-gap-6">
                    {content}
                  </BlockSection.NestedBlock>
                  <div className="wb-flex wb-flex-col wb-gap-3 md:wb-gap-5">
                    <BlockSection.NestedBlock className="wb-mx-auto">
                      {button}
                    </BlockSection.NestedBlock>
                    <BlockSection.NestedBlock className="wb-mx-auto">
                      {buttonDescription}
                    </BlockSection.NestedBlock>
                  </div>
                </div>
              </div>
              <BlockSection.NestedBlock
                block={footer}
                as={Fragment}
                render={() => (
                  <div className="wb-mx-auto wb-flex wb-flex-col wb-justify-center wb-gap-10 md:wb-gap-16">
                    <div className="wb-flex wb-flex-col wb-items-center wb-gap-4 md:wb-gap-8 wb-text-center">
                      {footer}
                    </div>
                  </div>
                )}
              />
            </BlockSection.Content>
          </BlockSection.Container>
        </BlockSection.Background>
      </BlockSection>
    );
  },
);

SalesBlock12.displayName = "SalesBlock12";
